import React, { useEffect, useRef } from "react";
import { useMotionValue, animate } from "framer-motion";
import { wagonStyle } from "../lib/wagonColors";
import { hasBogieImbalance } from "../lib/bogie";

const SPEED = 165;

export const SimWagon = ({ w, geo, mode, selected, onClick, onDone, ariaLabel, selectable = false }) => {
  const start = (w.route && w.route[0]) || geo.rest(w.tk, w.slot);
  const mx = useMotionValue(start.x);
  const my = useMotionValue(start.y);
  const gRef = useRef(null);
  const lastKey = useRef(-1);

  useEffect(() => {
    const apply = () => {
      if (gRef.current) gRef.current.setAttribute("transform", `translate(${mx.get()} ${my.get()})`);
    };
    apply();
    const u1 = mx.on("change", apply);
    const u2 = my.on("change", apply);
    return () => {
      u1();
      u2();
    };
  }, [mx, my]);

  useEffect(() => {
    const route = w.route;
    if (!route || !route.length) return;
    if (lastKey.current === w.routeKey) return;
    lastKey.current = w.routeKey;

    if (route.length === 1) {
      mx.set(route[0].x);
      my.set(route[0].y);
      return;
    }
    const dist = [0];
    for (let i = 1; i < route.length; i++) {
      dist.push(dist[i - 1] + Math.hypot(route[i].x - route[i - 1].x, route[i].y - route[i - 1].y));
    }
    const total = dist[dist.length - 1] || 1;
    const times = dist.map((d) => d / total);
    const dur = Math.max(0.5, total / SPEED);
    mx.set(route[0].x);
    my.set(route[0].y);
    const cx = animate(mx, route.map((p) => p.x), { duration: dur, times, ease: "linear" });
    const cy = animate(my, route.map((p) => p.y), {
      duration: dur,
      times,
      ease: "linear",
      onComplete: () => onDone && onDone(w.id, w.phase),
    });
    return () => {
      cx.stop();
      cy.stop();
    };
  }, [w.routeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const s = wagonStyle(w, mode);
  const lowConf = (w.ocr_confidence || 1) < 0.7;
  const bogieSkew = hasBogieImbalance(w);
  const { ww, wh } = geo;

  const handleKey = (e) => {
    if (!selectable) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick(w);
    }
  };

  return (
    <g
      ref={gRef}
      transform={`translate(${start.x} ${start.y})`}
      role={selectable ? "button" : undefined}
      tabIndex={selectable ? 0 : undefined}
      aria-hidden={selectable ? undefined : true}
      aria-label={selectable ? ariaLabel : undefined}
      style={{ cursor: "pointer", outline: "none" }}
      className="wagon-btn"
      data-testid={`wagon-${w.number}`}
      onClick={() => onClick(w)}
      onKeyDown={handleKey}
    >
      <g transform={`translate(${-ww / 2} ${-wh / 2})`}>
        <rect
          width={ww}
          height={wh}
          rx="2.5"
          fill={s.bg}
          stroke={selected ? "#ffffff" : (s.stroke ?? "rgba(0,0,0,0.45)")}
          strokeWidth={selected ? 2 : (s.strokeWidth ?? 1)}
          strokeDasharray={s.strokeDasharray ?? "0"}
          style={{ filter: `drop-shadow(0 0 4px ${s.glow})` }}
          className="wagon-rect"
        />
        <text
          x={ww / 2}
          y={wh / 2 + 2.8}
          fontSize="7.5"
          textAnchor="middle"
          fill="rgba(255,255,255,0.95)"
          fontFamily="IBM Plex Mono"
          fontWeight="600"
          pointerEvents="none"
        >
          {w.number.slice(-4)}
        </text>
        {lowConf && <circle cx={ww - 2} cy="2.5" r="2.6" fill="#fbbf24" stroke="#000" strokeWidth="0.5" />}
        {bogieSkew && (
          <polygon
            points={`2.5,0 5,5 0,5`}
            fill="#fb923c"
            stroke="#000"
            strokeWidth="0.4"
            transform="translate(0.5 0.5)"
          />
        )}
        {w.owner === "foreign" && <rect x="-1" y={wh - 3} width="3.6" height="3.6" fill="#93c5fd" transform={`rotate(45 0.8 ${wh - 1.2})`} />}
        {selected && <rect x="-3" y="-3" width={ww + 6} height={wh + 6} rx="3.5" fill="none" stroke="#38bdf8" strokeWidth="1.5" />}
      </g>
    </g>
  );
};
